import type {VatReturn} from '@vat/engine/types';

/**
 * Reine Fabrik- und Konventions-Helfer für `vatReturns/{id}` (Spec 3.4).
 * Kein Angular, kein Firebase – Datum und Nutzer kommen immer als Parameter.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const VAT_PERIOD_KEY_PATTERN = /^(M(0[1-9]|1[0-2])|Q[1-4])$/;

const VAT_RETURN_ID_PATTERN = /^(.+)_(\d{4})_(M(?:0[1-9]|1[0-2])|Q[1-4])_v(\d+)$/;

export const periodTypeForKey = (periodKey: string): 'MONAT' | 'QUARTAL' => periodKey.startsWith('Q') ? 'QUARTAL' : 'MONAT';

export const buildVatReturnID = (landlordID: string, year: number, periodKey: string, version: number): string => {
    if (!landlordID) {
        throw new Error('buildVatReturnID: landlordID fehlt');
    }
    if (!Number.isInteger(year) || year < 2000) {
        throw new Error(`buildVatReturnID: year ungültig (${year})`);
    }
    if (!VAT_PERIOD_KEY_PATTERN.test(periodKey)) {
        throw new Error(`buildVatReturnID: periodKey ungültig (${periodKey})`);
    }
    if (!Number.isInteger(version) || version < 1) {
        throw new Error(`buildVatReturnID: version ungültig (${version})`);
    }
    return `${landlordID}_${year}_${periodKey}_v${version}`;
};

export const parseVatReturnID = (id: string): { landlordID: string; year: number; periodKey: string; version: number } | null => {
    const match = VAT_RETURN_ID_PATTERN.exec(id ?? '');
    if (!match) {
        return null;
    }
    return {landlordID: match[1], year: Number(match[2]), periodKey: match[3], version: Number(match[4])};
};

/** Firestore-Sondertypen, die nicht rekursiv normalisiert werden dürfen. */
const isOpaqueFirestoreValue = (value: unknown): boolean => {
    if (value instanceof Date) {
        return true;
    }
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return typeof candidate['toDate'] === 'function'           // Timestamp
        || typeof candidate['isEqual'] === 'function'          // FieldValue / GeoPoint / DocumentReference
        || typeof candidate['_methodName'] === 'string';       // FieldValue (modular)
};

/** `undefined → null` tief; Firestore verweigert `undefined`, `removeEmpty` würde `FieldValue` zerstören. */
export const normalizeUndefinedToNull = <T>(value: T): T => {
    if (value === undefined) {
        return null as unknown as T;
    }
    if (value === null || typeof value !== 'object' || isOpaqueFirestoreValue(value)) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(item => normalizeUndefinedToNull(item)) as unknown as T;
    }
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        result[key] = normalizeUndefinedToNull(entry);
    }
    return result as T;
};

/** Firestore-Timestamp (Instanz mit toDate) oder protokollloses Pendant `{seconds, nanoseconds}`. */
const toDateIfTimestamp = (value: unknown): Date | null => {
    if (typeof value !== 'object' || value === null || value instanceof Date || Array.isArray(value)) {
        return null;
    }
    const candidate = value as { toDate?: () => Date; seconds?: unknown; nanoseconds?: unknown };
    if (typeof candidate.toDate === 'function') {
        return candidate.toDate();
    }
    if (typeof candidate.seconds === 'number' && typeof candidate.nanoseconds === 'number' && Object.keys(candidate).length === 2) {
        return new Date(candidate.seconds * 1000 + Math.floor(candidate.nanoseconds / 1_000_000));
    }
    return null;
};

const normalizeDatesDeep = (value: unknown): unknown => {
    if (value === null || value === undefined || typeof value !== 'object' || value instanceof Date) {
        return value;
    }
    const asDate = toDateIfTimestamp(value);
    if (asDate) {
        return asDate;
    }
    if (Array.isArray(value)) {
        return value.map(normalizeDatesDeep);
    }
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        result[key] = normalizeDatesDeep(entry);
    }
    return result;
};

/**
 * Lesegrenze Firestore → Engine: wandelt alle Timestamps eines `vatReturns/{id}`-Dokuments
 * (startDate/endDate/dueDate, freigabe.at, uebermittelt.am, adjustments[].createdAt, checklist.*.at,
 * exports[].at, taxProfileSnapshot.setupCompletedAt, …) rekursiv in `Date`, damit Engine, Export
 * (`profileAt`, formatDateDE) und Hash mit einem einheitlichen Typ arbeiten.
 */
export const normalizeVatReturnDates = (raw: unknown): VatReturn | null =>
    raw === null || raw === undefined ? null : normalizeDatesDeep(raw) as VatReturn;

export interface BuildDraftVatReturnParams {
    customerID: string | null;
    landlordID: string;
    year: number;
    periodKey: string;
    startDate: Date;
    endDate: Date;
    dueDate: Date;
    uid: string;
    now: Date;
    engineVersion: string;
    version?: number;
    korrekturVonID?: string | null;
}

/** Vollständiges ENTWURF-Dokument nach Spec 3.4 – jedes optionale Feld explizit `null`. */
export const buildDraftVatReturn = (params: BuildDraftVatReturnParams): VatReturn => {
    const version = params.version ?? 1;
    return {
        id: buildVatReturnID(params.landlordID, params.year, params.periodKey, version),
        customerID: params.customerID ?? null,
        landlordID: params.landlordID,
        year: params.year,
        periodKey: params.periodKey,
        periodType: periodTypeForKey(params.periodKey),
        version,
        korrekturVonID: params.korrekturVonID ?? null,
        korrigiertDurchID: null,
        vorherigerStatus: null,
        startDate: params.startDate,
        endDate: params.endDate,
        dueDate: params.dueDate,
        status: 'ENTWURF',
        taxProfileSnapshot: null,
        kennzahlen: null,
        totals: null,
        checklist: {},
        adjustments: [],
        engineVersion: params.engineVersion,
        inputHash: null,
        bestaetigterInputHash: null,
        computedAt: null,
        freigabe: null,
        uebermittelt: null,
        exports: [],
        fristen: {taskID: null, reminderID: null},
        createdAt: params.now,
        updatedAt: params.now,
        createdBy: params.uid,
        updatedBy: params.uid,
    };
};
